import React, { useState } from 'react';
import { Flame, HandMetal, Rocket, Heart, Dumbbell, Star } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../lib/api';

export const CHEERS = [
  { key: 'fire', icon: Flame, label: 'On fire' },
  { key: 'clap', icon: HandMetal, label: 'Rock on' },
  { key: 'rocket', icon: Rocket, label: 'To the moon' },
  { key: 'heart', icon: Heart, label: 'Love it' },
  { key: 'muscle', icon: Dumbbell, label: 'Strong' },
  { key: 'star', icon: Star, label: 'Star saver' },
];
const CHEER_MAP = Object.fromEntries(CHEERS.map((c) => [c.key, c]));

export function Avatar({ user, size = 8 }) {
  const style = { width: size * 4, height: size * 4 };
  if (user?.avatar_url) return <img src={user.avatar_url} alt={user.name} style={style} className="rounded-full shrink-0 object-cover" />;
  return <div style={style} className="rounded-full shrink-0 bg-[#9333EA]/30 text-[#C8A6FF] flex items-center justify-center text-xs font-bold">{(user?.name || '?').charAt(0).toUpperCase()}</div>;
}

export function ProgressRing({ percent, size = 56 }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="shrink-0" data-testid="progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="5" fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#9333EA" strokeWidth="5" fill="none" strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`}
        strokeDasharray={c} strokeDashoffset={c - (c * Math.min(100, percent)) / 100} style={{ transition: 'stroke-dashoffset 800ms ease' }} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="700" fontFamily="monospace">{`${Math.round(percent || 0)}%`}</text>
    </svg>
  );
}

export function CheerList({ cheers }) {
  return (
    <div className="space-y-2" data-testid="cheer-list">
      {cheers.map((c) => {
        const Icon = CHEER_MAP[c.emoji]?.icon || Star;
        return (
          <div key={c.cheer_id} className="flex items-start gap-2" data-testid={`cheer-${c.cheer_id}`}>
            <Avatar user={c.from} size={6} />
            <div className="text-xs"><span className="text-white font-semibold">{c.from?.name}</span> <Icon className="inline h-3.5 w-3.5 text-amber-300 -mt-0.5" />{c.message && <p className="text-[#A19DB0]">{c.message}</p>}</div>
          </div>
        );
      })}
    </div>
  );
}

export function CheerComposer({ goalId, onSent }) {
  const [emoji, setEmoji] = useState('fire');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/social/vaults/${goalId}/cheer`, { emoji, message }); toast.success('Cheer sent!'); setMessage(''); onSent?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="cheer-composer">
      <div className="flex gap-1.5">
        {CHEERS.map(({ key, icon: Icon, label }) => (
          <button key={key} title={label} data-testid={`cheer-emoji-${key}`} onClick={() => setEmoji(key)}
            className={`h-9 w-9 rounded-full flex items-center justify-center border transition-all duration-200 ${emoji === key ? 'bg-amber-400/20 border-amber-400/60 scale-110' : 'border-white/10 hover:border-white/30'}`}>
            <Icon className="h-4 w-4 text-amber-300" />
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="cheer-message-input" value={message} maxLength={140} onChange={(e) => setMessage(e.target.value)} placeholder="Add a note (optional)" className="flex-1 h-9 rounded-lg bg-[#0A0A0F] border border-white/10 px-3 text-xs text-white placeholder:text-[#6F6981] focus:outline-none focus:border-[#9333EA]" />
        <button data-testid="cheer-send-btn" disabled={busy} onClick={send} className="px-4 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400 transition-colors disabled:opacity-50">Cheer</button>
      </div>
    </div>
  );
}
